import React, {useState} from 'react';
import Page from "components/Page";
import Card from "components/Card";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {ChartPieIcon,DownloadIcon, EyeIcon} from "@heroicons/react/outline";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {TimeSeriesCriteria} from "types/OperatorInsights";
import {DateTime} from "luxon";
import AppImpressionsChart from "components/Chart/AppImpressionsChart";
import AppDownloadsChart from "components/Chart/AppDownloadsChart";
import AppConversionsChart from "components/Chart/AppConversionsChart";
import ExportButton from "../../../components/ExportButton";

// Helpers ----

const breadcrumbs = {"Performance": "/", "Mobile App": "/insights/App"}

// Component ----

function AppInsights() {
  const [criteria] = useState<TimeSeriesCriteria>({
    startAt: DateTime.now().minus({days: 90}),
    endAt: DateTime.now().endOf('day'),
    group: 'week',
    stats: [
      'app-impressions', 'app-downloads', 'app-conversion-rate',
      'android-app-impressions', 'android-app-downloads', 'android-app-conversion-rate',
      'ios-app-impressions', 'ios-app-downloads', 'ios-app-conversion-rate'
    ],
  });

  const {data: timeSeries, isLoading} = useQuery(
    ['insights', 'app', criteria],
    () => ngage.insights.timeSeries(criteria),
    {keepPreviousData: true}
  )

  const actions = (
    <ExportButton
      getData={() => ngage.insights.timeSeriesExport(criteria, 'csv')}
      dataType={'text/csv'}
      prefix={'ngage-app-insights'}
      extension={'csv'}
    />
  );

  return (
    <Page title="Mobile App" breadcrumbs={breadcrumbs} actions={actions} isLoading={isLoading}>

      {/* Stats */}
      <div className="mt-4">
        <StatList columns={3}>
          <StatBox
            name="Impressions"
            icon={EyeIcon}
            stat={timeSeries?.totals.app_impressions.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of times your app has been viewed on any app store.`}
          />

          <StatBox
            name="Downloads"
            icon={DownloadIcon}
            stat={timeSeries?.totals.app_downloads.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of times your app has been downloaded from any app store.`}
          />

          <StatBox
            name="Conversion Rate"
            icon={ChartPieIcon}
            stat={timeSeries?.averages.app_conversion_rate.toLocaleString() + '%'}
            isLoading={isLoading}
            tooltip={`The rate at which people download your app having seen it on any app store, for the last 90 days.`}
          />

          <StatBox
            name="Android Impressions"
            icon={EyeIcon}
            stat={timeSeries?.totals.android_app_impressions.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of times your app has been viewed on Google Play.`}
          />

          <StatBox
            name="Android Downloads"
            icon={DownloadIcon}
            stat={timeSeries?.totals.android_app_downloads.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of times your app has been downloaded from Google Play.`}
          />

          <StatBox
            name="Android Conversion Rate"
            icon={ChartPieIcon}
            stat={timeSeries?.averages.android_app_conversion_rate.toLocaleString() + '%'}
            isLoading={isLoading}
            tooltip={`The rate at which people download your app having seen it on Google Play, for the last 90 days.`}
          />

          <StatBox
            name="iOS Impressions"
            icon={EyeIcon}
            stat={timeSeries?.totals.ios_app_impressions.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of times your app has been viewed on The App Store.`}
          />

          <StatBox
            name="iOS Downloads"
            icon={DownloadIcon}
            stat={timeSeries?.totals.ios_app_downloads.toLocaleString()}
            isLoading={isLoading}
            tooltip={`The number of times your app has been downloaded from The App Store.`}
          />

          <StatBox
            name="iOS Conversion Rate"
            icon={ChartPieIcon}
            stat={timeSeries?.averages.ios_app_conversion_rate.toLocaleString() + '%'}
            isLoading={isLoading}
            tooltip={`The rate at which people download your app having seen it on The App Store, for the last 90 days.`}
          />
        </StatList>

        {/* Charts */}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">
          <Card
            heading="Impressions"
            subheading={`New impressions per ${criteria.group} over the last 90 days`}
          >
            {timeSeries &&
              <AppImpressionsChart
                data={timeSeries?.data.filter(point => point.name === 'app-impressions')}
                androidData={timeSeries?.data.filter(point => point.name === 'android-app-impressions')}
                iosData={timeSeries?.data.filter(point => point.name === 'ios-app-impressions')}
              />
            }
          </Card>

          <Card
            heading="Downloads"
            subheading={`New downloads per ${criteria.group} over the last 90 days`}
          >
            {timeSeries &&
              <AppDownloadsChart
                data={timeSeries?.data.filter(point => point.name === 'app-downloads')}
                androidData={timeSeries?.data.filter(point => point.name === 'android-app-downloads')}
                iosData={timeSeries?.data.filter(point => point.name === 'ios-app-downloads')}
              />
            }
          </Card>

          <Card
            heading="Conversion Rate"
            subheading={`Average conversion rate per ${criteria.group} over the last 90 days`}
          >
            {timeSeries &&
              <AppConversionsChart
                data={timeSeries?.data.filter(point => point.name === 'app-conversion-rate')}
                androidData={timeSeries?.data.filter(point => point.name === 'android-app-conversion-rate')}
                iosData={timeSeries?.data.filter(point => point.name === 'ios-app-conversion-rate')}
              />
            }
          </Card>
        </div>
      </div>
    </Page>
  );
}

export default AppInsights;